import type { AchievementView, LeaderboardView, MatchView } from '@mogzo/shared';
import type { BoardInfo, OverlayData } from './panels';

/** A friend's match waiting for them, for the preview of the multiplayer panel. */
const SAMPLE_MATCH: MatchView = {
  id: '00000000-0000-4000-8000-000000000031',
  mode: { id: 'duel', name: 'Duel', kind: 'turns', minPlayers: 2, maxPlayers: 2 },
  status: 'waiting',
  how: 'invite',
  code: 'K7QM2X',
  inviteUrl: 'https://mogzo.com/match/K7QM2X',
  players: [
    {
      playerId: 'sample-me',
      name: 'Alex',
      me: true,
      host: true,
      place: null,
      score: null,
      left: false,
    },
  ],
  waited: 12,
  results: false,
  rematch: null,
};

const BOARD: BoardInfo = {
  id: 'weekly-best',
  name: 'Best this week',
  sort: 'desc',
  format: 'number',
  reset: 'weekly',
};

const SCORES: [string, number][] = [
  ['Mira', 212],
  ['Ana', 188],
  ['Jon', 151],
  ['Sam', 120],
  ['Priya', 96],
  ['Leo', 88],
  ['You', 64],
  ['Kai', 51],
  ['Zoe', 40],
  ['Ravi', 33],
];

/**
 * Made-up scores and achievements, for the dashboard's preview of a game's look. `iconBase` is
 * the API address that serves MOGZO's achievement icons.
 */
export function sampleData(iconBase: string): OverlayData {
  const icon = (name: string, color: string) =>
    `${iconBase}/v1/achievement-icons/${name}.svg?color=${color}`;
  const achievements: AchievementView[] = [
    {
      id: 'first-flight',
      name: 'First flight',
      description: 'Finish your first run.',
      points: 5,
      hidden: false,
      steps: null,
      icon: icon('rocket', 'sky'),
      progress: 1,
      unlocked: true,
      unlockedAt: '2026-10-27T10:00:00.000Z',
    },
    {
      id: 'close-call',
      name: 'Close call',
      description: 'Hop away from a rock at the last moment.',
      points: 10,
      hidden: false,
      steps: null,
      icon: icon('zap', 'violet'),
      progress: 1,
      unlocked: true,
      unlockedAt: '2026-10-27T10:05:00.000Z',
    },
    {
      id: 'star-collector',
      name: 'Star collector',
      description: 'Grab 100 stars.',
      points: 25,
      hidden: false,
      steps: 100,
      icon: icon('star', 'pink'),
      progress: 37,
      unlocked: false,
      unlockedAt: null,
    },
    {
      id: 'score-50',
      name: 'Fifty',
      description: 'Score 50 in one run.',
      points: 25,
      hidden: false,
      steps: null,
      icon: icon('trophy', 'yellow'),
      progress: 0,
      unlocked: false,
      unlockedAt: null,
    },
  ];
  return {
    boards: async () => [BOARD],
    leaderboard: async (_id, view, period, limit): Promise<LeaderboardView> => {
      const rows = SCORES.map(([name, score], i) => ({
        rank: i + 1,
        playerId: `sample-${i}`,
        name: name === 'You' ? 'Alex' : name,
        score: period === 'previous' ? Math.round(score * 1.1) : score,
        me: name === 'You',
      }));
      const start = view === 'around' ? Math.max(0, 6 - Math.floor(limit / 2)) : 0;
      return { board: { ...BOARD, period: '2026-W44' }, entries: rows.slice(start, start + limit) };
    },
    achievements: async () => achievements,
    // The preview stays on the invite code: nobody joins, and nothing leaves it.
    multiplayer: {
      find: async () => SAMPLE_MATCH,
      create: async () => SAMPLE_MATCH,
      join: async () => SAMPLE_MATCH,
      poll: async () => SAMPLE_MATCH,
      start: async () => SAMPLE_MATCH,
      leave: async () => undefined,
      rematch: async () => SAMPLE_MATCH,
    },
  };
}
